/**
 * RoutePlannerPage - Enterprise Route Planning & Optimization
 *
 * Two-column layout:
 * 1. Left: Sortable stop list (reorderable)
 * 2. Right: Google Maps with route visualization
 *
 * Features:
 * - Drag & drop to reorder stops
 * - Google Directions API for ≤23 waypoints
 * - Google Route Optimization API for >23 waypoints
 * - Configurable stoppage times per chauffeur/stop
 * - Time window validation with visual warnings
 * - Capacity tracking with overflow warnings
 */

import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Card, CardHeader, CardTitle, CardContent, Button, Spinner, Avatar } from '../components/ui';
import { CallButton } from '../components/phone/CallButton';
import { useColleagues } from '../hooks/useColleagues';
import {
  StopList,
  RouteMap,
  RoutePlannerToolbar,
  RoutePlannerDrawerPanel,
  StopSelectionActionBar,
  NietGeluktDialog,
  TochGeslaagdDialog,
  MoveStopsModal,
  MoveUndoBanner,
  FinalizeRouteModal,
  BijrijderDialog,
} from '../components/route-planner';
import { MergeRoutesModal } from '../components/calendar';
import { RouteDuur } from '../components/calendar/RouteDuur';
import { minutenMerk } from '../components/calendar/RouteMetriek';
import { useRoutePlannerStore, useCalendarStore } from '../stores';
import { useCapacityWarning, useTimeWindowViolations } from '../stores/route-planner-store';
import {
  useRoutePlannerDrawerStore,
  encodeDrawerParam,
  parseDrawerParam,
} from '../stores/route-planner-drawer-store';
import type { RouteListItem } from '../lib/salesforce';
import { salesforceClient } from '../lib/salesforce';
import { formatTijd } from '../lib/format';

// ============================================================================
// ICONS
// ============================================================================

const ArrowLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

// ============================================================================
// CAPACITY DISPLAY COMPONENT
// ============================================================================

function CapacityBadge({
  label,
  icon,
  count,
  remaining,
  override,
  onOverrideChange,
  disabled,
}: {
  label: string;
  icon: string;
  count: number;
  remaining: number | null | undefined;
  override: number | null | undefined;
  onOverrideChange: (value: number | null) => void;
  disabled: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalize: treat undefined as null
  const safeRemaining = remaining != null ? remaining : null;
  const safeOverride = override != null ? override : null;

  // Calculate total capacity from override or from count+remaining
  const total = safeOverride != null
    ? safeOverride
    : safeRemaining != null
    ? count + safeRemaining
    : null;

  const isFull = total != null && count >= total;
  const isLow = total != null && !isFull && (total - count) <= 3;
  const freeSpots = total != null ? total - count : null;

  // Progress percentage for bar
  const progressPct = total != null && total > 0
    ? Math.min((count / total) * 100, 100)
    : null;

  const handleStartEdit = () => {
    if (disabled) return;
    setEditValue(safeOverride != null ? String(safeOverride) : '');
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSave = () => {
    setIsEditing(false);
    const num = editValue.trim() === '' ? null : parseInt(editValue, 10);
    if (num !== null && isNaN(num)) return;
    onOverrideChange(num);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setIsEditing(false);
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
        isFull
          ? 'bg-red-50 border-red-200 text-red-700'
          : isLow
          ? 'bg-amber-50 border-amber-200 text-amber-700'
          : 'bg-gray-50 border-gray-200 text-gray-700'
      }`}
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="text-xs font-medium whitespace-nowrap">{label}</span>

      {/* Count / Total */}
      <span className="text-sm font-bold tabular-nums">{count}</span>
      {total != null && (
        <span className="text-xs opacity-60 tabular-nums">/ {total}</span>
      )}

      {/* Mini progress bar */}
      {progressPct != null && (
        <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isFull ? 'bg-red-500' : isLow ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      {/* Free spots badge */}
      {freeSpots != null && (
        <span
          className={`text-xs font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
            isFull
              ? 'bg-red-200 text-red-800'
              : isLow
              ? 'bg-amber-200 text-amber-800'
              : 'bg-emerald-100 text-emerald-700'
          }`}
        >
          {freeSpots <= 0 ? 'vol' : `${freeSpots} vrij`}
        </span>
      )}

      {/* Edit override */}
      {!disabled && (
        isEditing ? (
          <input
            ref={inputRef}
            type="number"
            min={0}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="w-14 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white text-gray-900"
            placeholder="max"
            title="Capaciteit override instellen (leeg = standaard)"
          />
        ) : (
          <button
            onClick={handleStartEdit}
            className="flex items-center gap-0.5 text-xs text-gray-400 hover:text-indigo-600 transition-colors"
            title={`Max capaciteit aanpassen${safeOverride != null ? ` (huidig: ${safeOverride})` : ''}`}
          >
            <PencilIcon />
          </button>
        )
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function RoutePlannerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const routeId = searchParams.get('routeId');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [bijrijderOpen, setBijrijderOpen] = useState(false);
  const selectedStopIds = useRoutePlannerStore((s) => s.selectedStopIds);

  // Drawer state + URL sync
  const drawerOpenItem = useRoutePlannerDrawerStore((s) => s.openItem);
  const openDrawer = useRoutePlannerDrawerStore((s) => s.open);
  const closeDrawer = useRoutePlannerDrawerStore((s) => s.close);
  const isDrawerOpen = drawerOpenItem !== null;

  const openParam = searchParams.get('open');
  const klantTypeParam = searchParams.get('klantType');

  // URL → store
  useEffect(() => {
    const parsed = parseDrawerParam(openParam, klantTypeParam);
    const current = useRoutePlannerDrawerStore.getState().openItem;
    const same = !!(parsed && current
      && parsed.type === current.type
      && parsed.id === current.id
      && parsed.klantType === current.klantType);
    if (parsed && !same) openDrawer(parsed);
    else if (!parsed && current) closeDrawer();
  }, [openParam, klantTypeParam, openDrawer, closeDrawer]);

  // Store → URL
  useEffect(() => {
    const desired = encodeDrawerParam(drawerOpenItem);
    const currentOpen = searchParams.get('open');
    const currentKT = searchParams.get('klantType');
    const desiredKT = drawerOpenItem?.type === 'klant' ? (drawerOpenItem.klantType ?? 'account') : null;
    if (desired === currentOpen && desiredKT === currentKT) return;
    const next = new URLSearchParams(searchParams);
    if (desired) next.set('open', desired); else next.delete('open');
    if (desiredKT) next.set('klantType', desiredKT); else next.delete('klantType');
    setSearchParams(next, { replace: true });
  }, [drawerOpenItem, searchParams, setSearchParams]);

  // Reset drawer on unmount
  useEffect(() => {
    return () => {
      useRoutePlannerDrawerStore.getState().close();
    };
  }, []);

  const {
    route,
    stops,
    isLoading,
    error,
    isLocked,
    totalJourneyTime,
    totalKilometers,
    departureTime,
    chauffeurs,
    isSaving,
    isFinalizing,
    lastFinalizeResult,
    capaciteitResterendOphalen,
    capaciteitOverrideOphalen,
    capaciteitResterendAfleveren,
    capaciteitOverrideAfleveren,
    ophalenNietVoorstellen,
    afleverenNietVoorstellen,
    loadRoute,
    loadChauffeurs,
    reorderStops,
    nietGeluktStopIds,
    closeNietGeluktDialog,
    markStopsNietGelukt,
    isMarkingNietGelukt,
    tochGeslaagdStopIds,
    closeTochGeslaagdDialog,
    markStopsTochGeslaagd,
    isMarkingTochGeslaagd,
    updateChauffeur,
    updateCapacityOverride,
    updateNietVoorstellen,
    setBijrijder,
    finalizeRoute,
    clearLastFinalizeResult,
    clearError,
    reset,
  } = useRoutePlannerStore();

  // Chauffeur bellen: join route.chauffeurId (SF Medewerker__c-Id) → collega-nummer.
  const { byMedewerkerId } = useColleagues();
  const chauffeurPhone = route?.chauffeurId
    ? byMedewerkerId.get(route.chauffeurId)?.phone ?? null
    : null;

  // Always compute stop counts from the local stops array (works without Apex deploy)
  const localStopCountOphalen = useMemo(() => stops.filter(s => s.stopType === 'Ophalen').length, [stops]);
  const localStopCountAfleveren = useMemo(() => stops.filter(s => s.stopType === 'Terugbrengen').length, [stops]);

  // Calculate end time: last stop departure time + travel time to depot
  const endTime = useMemo(() => {
    if (stops.length === 0) return null;

    const lastStop = stops[stops.length - 1];
    if (!lastStop?.departureTime || !lastStop?.hourAndMinutes) return null;

    try {
      // Parse last stop departure time (ISO datetime string like "2026-01-30T14:48:00")
      const depDateTime = new Date(lastStop.departureTime);

      // Parse hourAndMinutes like "0 hr 11 min" (travel time to depot)
      const hourMatch = lastStop.hourAndMinutes.match(/(\d+)\s*hr/);
      const minMatch = lastStop.hourAndMinutes.match(/(\d+)\s*min/);
      const travelHours = hourMatch?.[1] ? parseInt(hourMatch[1], 10) : 0;
      const travelMinutes = minMatch?.[1] ? parseInt(minMatch[1], 10) : 0;

      // Add travel time to depot
      const arrivalAtDepot = new Date(depDateTime.getTime() + (travelHours * 60 + travelMinutes) * 60 * 1000);
      return formatTijd(arrivalAtDepot);
    } catch {
      return null;
    }
  }, [stops]);

  const {
    routesByDate,
    fetchRoutes,
    isLoading: calendarLoading,
  } = useCalendarStore();

  // Warnings
  const capacityWarning = useCapacityWarning();
  const timeWindowViolations = useTimeWindowViolations();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get all routes as a flat array
  const allRoutes = useMemo(() => {
    const routes: RouteListItem[] = [];
    Object.values(routesByDate).forEach(dateRoutes => {
      routes.push(...dateRoutes);
    });
    return routes.sort((a, b) => a.datum.localeCompare(b.datum));
  }, [routesByDate]);

  // Fetch available routes for selector
  useEffect(() => {
    fetchRoutes();
    loadChauffeurs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load route when routeId changes
  useEffect(() => {
    if (routeId) {
      loadRoute(routeId);
    } else {
      reset();
    }

    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Handle drag end for reordering stops
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    // Check if it's a reorder within stops
    if (active.id !== over.id) {
      const oldIndex = stops.findIndex(s => s.id === active.id);
      const newIndex = stops.findIndex(s => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderStops(oldIndex, newIndex);
      }
    }
  }, [stops, reorderStops]);

  const handleRouteSelect = (selectedRouteId: string) => {
    if (selectedRouteId) {
      setSearchParams({ routeId: selectedRouteId });
    } else {
      setSearchParams({});
    }
  };

  // No route selected - show route selector
  if (!routeId) {
    return (
      <>
        <div className="p-6 max-w-2xl mx-auto">
          <h1 className="text-2xl font-heading font-semibold text-main mb-6">
            Route Planner
          </h1>

          <Card variant="elevated" padding="none">
            <CardHeader className="px-6 pt-6">
              <CardTitle>Selecteer een route</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {calendarLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : allRoutes.length === 0 ? (
                <p className="text-muted py-4">
                  Geen routes gevonden voor deze week
                </p>
              ) : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {allRoutes.map((r: RouteListItem) => (
                    <button
                      key={r.id}
                      onClick={() => handleRouteSelect(r.id)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors text-left"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-main">{r.routeName || r.name}</p>
                          {r.locked && (
                            <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 rounded">
                              Vergrendeld
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted">
                          {format(new Date(r.datum), 'EEEE d MMM', { locale: nl })} • {r.chauffeurName || 'Geen chauffeur'} • {r.stopCount} stops
                        </p>
                        <div className="mt-1">
                          <RouteDuur route={r} variant="compact" />
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Loading route
  if (isLoading) {
    return (
      <>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <Spinner size="lg" />
            <p className="mt-4 text-muted">Route laden...</p>
          </div>
        </div>
      </>
    );
  }

  // Error loading route (only show full-page error when route hasn't loaded yet)
  if (error && !route) {
    return (
      <>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <p className="text-error mb-4">{error}</p>
            <Button variant="secondary" onClick={() => navigate('/route-planner')}>
              Terug naar route selectie
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Route loaded - show enterprise planner with 3-column layout
  // Salesforce Route link
  const salesforceRouteLink = route?.id
    ? `https://cinco.lightning.force.com/lightning/r/Routes__c/${route.id}/view`
    : null;

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Inline error banner for action errors (optimization, save, etc.) */}
        {error && route && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-red-800">{error}</p>
            <button onClick={clearError} className="text-red-600 hover:text-red-800 text-sm font-medium">
              Sluiten
            </button>
          </div>
        )}
        {/* Undo banner shown for 60s after a cross-route stops move */}
        <MoveUndoBanner />
        {/* Header with back button and route info */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* Back + title */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/route-planner')}
              className="flex items-center gap-2 text-muted hover:text-main transition-colors flex-shrink-0"
            >
              <ArrowLeftIcon />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-main truncate">
                  {route?.routeName || route?.name}
                </h1>
                {salesforceRouteLink && (
                  <a
                    href={salesforceRouteLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                    title="Openen in Salesforce"
                  >
                    <ExternalLinkIcon />
                  </a>
                )}
              </div>
              <p className="text-xs text-muted truncate">
                {route?.datum && format(new Date(route.datum), 'EEEE d MMMM yyyy', { locale: nl })}
              </p>
            </div>
          </div>

          {/* Chauffeur selector */}
          <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
            {route?.chauffeurName && (
              <Avatar name={route.chauffeurName} size="sm" />
            )}
            <select
              value={route?.chauffeurId || ''}
              onChange={(e) => updateChauffeur(e.target.value)}
              disabled={isSaving || isLocked}
              className={`text-sm border border-gray-200 rounded-md px-2 py-1.5 max-w-[160px] ${
                isSaving || isLocked
                  ? 'bg-gray-50 text-gray-600 cursor-not-allowed'
                  : 'bg-white hover:border-gray-300 cursor-pointer'
              }`}
              title={isLocked ? 'Route is vergrendeld' : 'Chauffeur wijzigen'}
            >
              <option value="">Geen chauffeur</option>
              {chauffeurs.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {chauffeurPhone && (
              <CallButton
                number={chauffeurPhone}
                brand="CINCO"
                customerName={route?.chauffeurName ?? undefined}
                sourceContext="route-chauffeur"
                variant="icon"
                title={`Bel ${route?.chauffeurName ?? 'chauffeur'}`}
              />
            )}
            {isSaving && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            )}
          </div>

          {/* Bijrijder badge + edit */}
          {route && (
            <button
              type="button"
              onClick={() => setBijrijderOpen(true)}
              disabled={isSaving}
              title={
                route.bijrijderNodig
                  ? `Bijrijder: ${route.bijrijderName ?? 'nog niet toegewezen'} — klik om te wijzigen`
                  : 'Bijrijder toevoegen aan deze route'
              }
              className={`text-sm px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${
                route.bijrijderNodig
                  ? route.bijrijderName
                    ? 'border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100'
                    : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span aria-hidden>👥</span>
              <span>{route.bijrijderNodig ? (route.bijrijderName ?? 'Bijrijder TBD') : 'Bijrijder'}</span>
            </button>
          )}

          {/* Merge action */}
          {route && (
            <button
              type="button"
              onClick={() => setMergeOpen(true)}
              disabled={isLocked}
              title={isLocked ? 'Route is vergrendeld' : 'Samenvoegen met andere route op deze dag'}
              className="text-sm px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Samenvoegen met…
            </button>
          )}

          {/* Finalize action — alleen voor niet-afgeronde routes */}
          {route && (
            <button
              type="button"
              onClick={() => setFinalizeOpen(true)}
              disabled={
                isFinalizing ||
                route.status === 'Afgerond' ||
                route.status === 'Geannuleerd'
              }
              title={
                route.status === 'Afgerond'
                  ? 'Route is al afgerond'
                  : route.status === 'Geannuleerd'
                  ? 'Route is geannuleerd'
                  : 'Route afronden — opgehaalde kleden door naar Voorinspectie'
              }
              className="text-sm px-2.5 py-1.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Route afronden
            </button>
          )}

          {/* Klantupdates onderweg pauzeren (plan 2026-10-12, Decision 22): als een
              chauffeur uitvalt of kantoor zelf herschikt. Reden verplicht; audit via
              het PATCH-pad. De herijk rekent door, de berichten slaan over. */}
          {route && (route.status === 'Onderweg' || route.vertrokkenOp || route.herijktOp || route.autoUpdatesPauze) && (
            <button
              type="button"
              disabled={isSaving}
              title={route.autoUpdatesPauze
                ? `Automatische klantberichten onderweg staan uit${route.autoUpdatesPauzeReden ? `: ${route.autoUpdatesPauzeReden}` : ''}. Klik om te hervatten.`
                : 'Pauzeer de automatische klantberichten onderweg (afwijking, eerder, onderweg) voor deze route'}
              onClick={async () => {
                try {
                  if (route.autoUpdatesPauze) {
                    await salesforceClient.updateRoute(route.id, { autoUpdatesPauze: false });
                  } else {
                    const reden = window.prompt('Waarom pauzeer je de automatische klantberichten voor deze route?');
                    if (!reden || !reden.trim()) return;
                    await salesforceClient.updateRoute(route.id, { autoUpdatesPauze: true, autoUpdatesPauzeReden: reden.trim() });
                  }
                  await loadRoute(route.id);
                } catch (e) { useRoutePlannerStore.setState({ error: e instanceof Error ? e.message : 'Pauzeren mislukt' }); }
              }}
              className={`text-sm px-2.5 py-1.5 rounded-md border ${route.autoUpdatesPauze
                ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {route.autoUpdatesPauze ? 'Klantupdates gepauzeerd · Hervatten' : 'Klantupdates pauzeren'}
            </button>
          )}

          {/* Capacity indicators */}
          {route && (route.capaciteitAutoPauzeOphalen || route.capaciteitAutoPauzeAfleveren) && (
            <div className="flex gap-2 text-xs text-amber-700">
              {(['ophalen', 'afleveren'] as const).map(type => (
                (type === 'ophalen' ? route.capaciteitAutoPauzeOphalen : route.capaciteitAutoPauzeAfleveren) &&
                <button key={type} disabled={isSaving} className="underline" onClick={async () => {
                  try {
                    await salesforceClient.updateRoute(route.id, type === 'ophalen'
                      ? { capaciteitAutoHervatOphalen: true } : { capaciteitAutoHervatAfleveren: true });
                    await loadRoute(route.id);
                  } catch (e) { useRoutePlannerStore.setState({ error: e instanceof Error ? e.message : 'Hervatten mislukt' }); }
                }}>Automatisch verruimen {type} gepauzeerd · Hervatten</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <CapacityBadge
              label="Ophalen"
              icon="↑"
              count={localStopCountOphalen}
              remaining={capaciteitResterendOphalen}
              override={capaciteitOverrideOphalen}
              onOverrideChange={(val) => updateCapacityOverride('ophalen', val)}
              disabled={isLocked}
            />
            <CapacityBadge
              label="Afleveren"
              icon="↓"
              count={localStopCountAfleveren}
              remaining={capaciteitResterendAfleveren}
              override={capaciteitOverrideAfleveren}
              onOverrideChange={(val) => updateCapacityOverride('afleveren', val)}
              disabled={isLocked}
            />
            {/* Minuten-poort: wat de klant nog aangeboden krijgt, naast de stoptelling. */}
            {(['ophalen', 'afleveren'] as const).map((type) => {
              const m = minutenMerk(route?.minutenStand?.[type]);
              return m ? (
                <span key={type} className="inline-flex items-center gap-1 text-[10px] text-gray-500" title={m.title}>
                  {type === 'ophalen' ? '↑' : '↓'} {m.merk}
                </span>
              ) : null;
            })}
          </div>

          {/* Niet-voorstellen toggles */}
          <div className="flex items-center gap-3 flex-wrap pl-3 border-l border-gray-200">
            <button
              onClick={() => updateNietVoorstellen('ophalen', !ophalenNietVoorstellen)}
              disabled={isSaving}
              className="flex items-center gap-2 cursor-pointer select-none disabled:opacity-50"
              title="Ophalen niet meer voorstellen op deze route"
            >
              <span className={`text-xs whitespace-nowrap ${ophalenNietVoorstellen ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                ↑ Niet voorstellen
              </span>
              <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${ophalenNietVoorstellen ? 'bg-red-500' : 'bg-gray-300'}`}>
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${ophalenNietVoorstellen ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
              </div>
            </button>
            <button
              onClick={() => updateNietVoorstellen('afleveren', !afleverenNietVoorstellen)}
              disabled={isSaving}
              className="flex items-center gap-2 cursor-pointer select-none disabled:opacity-50"
              title="Afleveren niet meer voorstellen op deze route"
            >
              <span className={`text-xs whitespace-nowrap ${afleverenNietVoorstellen ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                ↓ Niet voorstellen
              </span>
              <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${afleverenNietVoorstellen ? 'bg-red-500' : 'bg-gray-300'}`}>
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${afleverenNietVoorstellen ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
              </div>
            </button>
          </div>

          {/* Stats & warnings */}
          <div className="flex items-center gap-3 flex-wrap ml-auto">
            {/* Time window warnings */}
            {timeWindowViolations > 0 && (
              <div className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2 py-1 rounded text-sm">
                <AlertTriangleIcon />
                <span>{timeWindowViolations} tijdsvenster waarschuwing(en)</span>
              </div>
            )}

            {/* Capacity warning */}
            {capacityWarning && (
              <div className="flex items-center gap-1.5 text-red-600 bg-red-50 px-2 py-1 rounded text-sm">
                <AlertTriangleIcon />
                <span>Capaciteit overschreden</span>
              </div>
            )}

            {/* Locked summary: Voornaam: Routenaam • Tijd Plaats • stats */}
            {isLocked && (() => {
              const chauffeurFirstName = route?.chauffeurName?.split(' ')[0] || '';
              const routeLabel = route?.routeName || route?.name || '';
              const firstStop = stops.length > 0 ? stops[0] : null;
              let firstStopTime = '';
              if (firstStop?.arrivalTime) {
                try {
                  firstStopTime = formatTijd(firstStop.arrivalTime).replace(/^0/, '');
                } catch { /* ignore */ }
              }
              const firstStopCity = firstStop?.city || '';
              const parts: string[] = [];
              if (chauffeurFirstName && routeLabel) {
                parts.push(`${chauffeurFirstName}: ${routeLabel}`);
              }
              if (firstStopTime && firstStopCity) {
                parts.push(`${firstStopTime} ${firstStopCity}`);
              }
              parts.push(`${stops.length} stops`);
              if (totalJourneyTime) parts.push(totalJourneyTime);
              if (totalKilometers !== null) parts.push(`${totalKilometers.toFixed(1)} km`);
              if (departureTime && endTime) parts.push(`${departureTime} - ${endTime}`);

              return (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{parts.join(' • ')}</span>
                </div>
              );
            })()}

            {/* Stats (unlocked) */}
            {!isLocked && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">{stops.length}</span> stops
                {totalJourneyTime && (
                  <> • <span className="font-medium">{totalJourneyTime}</span></>
                )}
                {totalKilometers !== null && (
                  <> • <span className="font-medium">{totalKilometers.toFixed(1)} km</span></>
                )}
                {departureTime && endTime && (
                  <> • <span className="font-medium">{departureTime} - {endTime}</span></>
                )}
              </div>
            )}

            {/* Levende route: verwachte dagduur tegen het budget */}
            {route && route.duurMinuten != null && (
              <RouteDuur route={route} variant="header" stil={isLocked} />
            )}

            {/* Lock indicator */}
            {isLocked && (
              <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                🔒 Vergrendeld
              </span>
            )}
          </div>
        </div>

        {/* Toolbar */}
        <RoutePlannerToolbar />

        {/* Main content - 2-column layout */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="flex-1 flex min-h-0">
            {/* Left: Sortable stop list - scrollable */}
            <div
              className="w-[400px] flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50"
            >
              <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h2 className="font-medium text-main">
                  Stops ({stops.length})
                </h2>
                {!isLocked && selectedStopIds.size === 0 && (
                  <span className="text-xs text-gray-500">
                    Sleep om te herordenen
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto flex flex-col">
                <div className="flex-1">
                  <SortableContext
                    items={stops.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <StopList />
                  </SortableContext>
                </div>
                <StopSelectionActionBar onMoveClick={() => setMoveOpen(true)} />
              </div>
            </div>

            {/* Right: Map - fills remaining space, fixed position */}
            <div className="flex-1 relative">
              <div className="absolute inset-0">
                <RouteMap />
              </div>
            </div>

            {/* Drawer: order or klant detail side-panel */}
            {isDrawerOpen && <RoutePlannerDrawerPanel />}
          </div>
        </DndContext>

        {mergeOpen && route && (
          <MergeRoutesModal
            sourceRoute={{
              id: route.id,
              name: route.name,
              routeName: route.routeName,
              datum: route.datum,
              chauffeurId: route.chauffeurId,
              chauffeurName: route.chauffeurName,
              locked: route.locked,
              status: route.status,
              stopCount: stops.length,
              hourAndMinutes: route.hourAndMinutes,
              startTimePlanned: null,
              endTimePlanned: null,
              capaciteitResterend: route.capaciteitResterend,
              capaciteitTotaal: route.capaciteitTotaal,
              stopCountOphalen: localStopCountOphalen,
              capaciteitResterendOphalen: route.capaciteitResterendOphalen,
              capaciteitOverrideOphalen: route.capaciteitOverrideOphalen,
              stopCountAfleveren: localStopCountAfleveren,
              capaciteitResterendAfleveren: route.capaciteitResterendAfleveren,
              capaciteitOverrideAfleveren: route.capaciteitOverrideAfleveren,
              stopsAddedAfterLock: 0,
              itemCountOphalen: 0,
              bijrijderNodig: route.bijrijderNodig,
              bijrijderId: route.bijrijderId,
              bijrijderName: route.bijrijderName,
            }}
            onClose={() => setMergeOpen(false)}
            onMerged={(targetRouteId) => {
              setSearchParams({ routeId: targetRouteId });
            }}
          />
        )}

        {finalizeOpen && route && (
          <FinalizeRouteModal
            open={finalizeOpen}
            routeName={route.routeName || route.name}
            isFinalizing={isFinalizing}
            onClose={() => {
              if (!isFinalizing) setFinalizeOpen(false);
            }}
            onConfirm={async () => {
              await finalizeRoute();
              setFinalizeOpen(false);
            }}
          />
        )}

        {bijrijderOpen && route && (
          <BijrijderDialog
            route={route}
            chauffeurs={chauffeurs}
            isOpen={bijrijderOpen}
            isSaving={isSaving}
            onClose={() => {
              if (!isSaving) setBijrijderOpen(false);
            }}
            onSave={async (input) => {
              await setBijrijder(input);
              setBijrijderOpen(false);
            }}
          />
        )}

        {lastFinalizeResult && (
          <div className="fixed bottom-4 right-4 z-40 max-w-sm bg-emerald-50 border border-emerald-200 rounded-lg shadow-lg p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-emerald-900">
                Route {lastFinalizeResult.routeName} afgerond
              </p>
              <p className="text-xs text-emerald-700 mt-0.5">
                {lastFinalizeResult.itemsUpdated} kleed
                {lastFinalizeResult.itemsUpdated === 1 ? '' : 'en'} door naar Voorinspectie.
              </p>
            </div>
            <button
              type="button"
              onClick={clearLastFinalizeResult}
              className="text-emerald-600 hover:text-emerald-800 text-sm flex-shrink-0"
              aria-label="Sluiten"
            >
              ✕
            </button>
          </div>
        )}

        {moveOpen && route && (
          <MoveStopsModal
            sourceRoute={{
              id: route.id,
              name: route.name,
              routeName: route.routeName,
              datum: route.datum,
              chauffeurId: route.chauffeurId,
              chauffeurName: route.chauffeurName,
              locked: route.locked,
              status: route.status,
              stopCount: stops.length,
              hourAndMinutes: route.hourAndMinutes,
              startTimePlanned: null,
              endTimePlanned: null,
              capaciteitResterend: route.capaciteitResterend,
              capaciteitTotaal: route.capaciteitTotaal,
              stopCountOphalen: localStopCountOphalen,
              capaciteitResterendOphalen: route.capaciteitResterendOphalen,
              capaciteitOverrideOphalen: route.capaciteitOverrideOphalen,
              stopCountAfleveren: localStopCountAfleveren,
              capaciteitResterendAfleveren: route.capaciteitResterendAfleveren,
              capaciteitOverrideAfleveren: route.capaciteitOverrideAfleveren,
              stopsAddedAfterLock: 0,
              itemCountOphalen: 0,
              bijrijderNodig: route.bijrijderNodig,
              bijrijderId: route.bijrijderId,
              bijrijderName: route.bijrijderName,
            }}
            selectedCount={selectedStopIds.size}
            onClose={() => setMoveOpen(false)}
            onMoved={() => setMoveOpen(false)}
          />
        )}

        {/* Niet gelukt — klant laten herplannen (per-stop én bulk) */}
        <NietGeluktDialog
          stops={nietGeluktStopIds ? stops.filter((s) => nietGeluktStopIds.includes(s.id)) : []}
          isOpen={nietGeluktStopIds !== null}
          onClose={closeNietGeluktDialog}
          isSubmitting={isMarkingNietGelukt}
          onConfirm={async (opts) => {
            if (!nietGeluktStopIds) return;
            await markStopsNietGelukt(nietGeluktStopIds, opts);
            closeNietGeluktDialog();
          }}
        />

        {/* Toch geslaagd — chauffeur ging alsnog langs (inverse van niet gelukt) */}
        <TochGeslaagdDialog
          stops={tochGeslaagdStopIds ? stops.filter((s) => tochGeslaagdStopIds.includes(s.id)) : []}
          isOpen={tochGeslaagdStopIds !== null}
          onClose={closeTochGeslaagdDialog}
          isSubmitting={isMarkingTochGeslaagd}
          onConfirm={async (opts) => {
            if (!tochGeslaagdStopIds) return;
            await markStopsTochGeslaagd(tochGeslaagdStopIds, opts);
            closeTochGeslaagdDialog();
          }}
        />
      </div>
    </>
  );
}
